import { apiErrorMessage, customFetch } from '@agos/api-client'
import { T, tt } from '@agos/shared'
import { Bike, Loader2, MailCheck } from 'lucide-react'
import { useState, type FormEvent } from 'react'

import {
  COURIER_SIGNUP_PATH,
  EMPTY_SIGNUP,
  SIGNUP_LABELS,
  signupBody,
  signupProblem,
  type CourierSignupForm,
} from './courier-signup'

const INPUT_CLASSES =
  'w-full bg-[#0f1117] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/30'
const LABEL_CLASSES = 'block text-sm text-gray-400 mb-1.5'

/**
 * `/kurier` - an external courier signs up here, outside the shell and before there is a session. `FRONTEND.md`
 * §9.786, owner O139: the restaurant sends him the link from *Externý šofér*, he fills this in, confirms his e-mail
 * and waits for the restaurant to approve him there.
 *
 * **The same answer whatever happened to the address** - the server's rule (a taken e-mail is not revealed), so the
 * page says *check your e-mail and wait for approval* every time it is accepted.
 */
export function CourierSignupPage() {
  const [form, setForm] = useState<CourierSignupForm>(EMPTY_SIGNUP)
  const [sending, setSending] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const problem = signupProblem(form)
    if (problem !== null) {
      setError(problem)
      return
    }
    setSending(true)
    setError(null)
    try {
      await customFetch<unknown>(COURIER_SIGNUP_PATH, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(signupBody(form)),
      })
      setDone(true)
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Registrácia sa nepodarila. Skúste to znova.')))
    }
    setSending(false)
  }

  const box = (field: keyof CourierSignupForm, type: string, autoComplete: string) => (
    <div key={field}>
      <label className={LABEL_CLASSES} htmlFor={`courier-${field}`}>
        {SIGNUP_LABELS[field]}
      </label>
      <input
        id={`courier-${field}`}
        data-courier-signup-field={field}
        type={type}
        autoComplete={autoComplete}
        value={form[field]}
        onChange={(event) => setForm((current) => ({ ...current, [field]: event.target.value }))}
        className={INPUT_CLASSES}
      />
    </div>
  )

  return (
    <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Bike className="text-amber-400" size={36} />
            <span className="text-2xl font-bold text-white">
              <T>Externý kuriér</T>
            </span>
          </div>
          <p className="text-gray-400 text-sm">
            <T>Rozvážajte objednávky tejto reštaurácie. Odmenu vidíte pri každej objednávke vopred.</T>
          </p>
        </div>

        <div className="bg-[#1a1d27] rounded-2xl border border-white/10 p-8 shadow-2xl">
          {done ? (
            <div className="text-center space-y-3" data-courier-signup-done>
              <MailCheck className="mx-auto text-emerald-400" size={40} />
              <h1 className="text-xl font-semibold text-white">
                <T>Skontrolujte e-mail</T>
              </h1>
              <p className="text-sm text-gray-400">
                <T>
                  Poslali sme vám odkaz na potvrdenie e-mailu. Potom počkajte na schválenie prevádzkou - prihlásiť sa
                  budete môcť, keď vás schváli.
                </T>
              </p>
              <a href="/login" className="inline-block text-sm text-amber-400 hover:underline">
                <T>Na prihlásenie</T>
              </a>
            </div>
          ) : (
            <form onSubmit={(event) => void submit(event)} className="space-y-4" data-courier-signup>
              <h1 className="text-xl font-semibold text-white">
                <T>Registrácia</T>
              </h1>
              <div className="grid grid-cols-2 gap-3">
                {box('firstName', 'text', 'given-name')}
                {box('lastName', 'text', 'family-name')}
              </div>
              {box('phone', 'tel', 'tel')}
              {box('email', 'email', 'email')}
              {box('password', 'password', 'new-password')}
              {error !== null && (
                <p className="text-sm text-red-400" role="alert" data-courier-signup-error>
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={sending}
                className="w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-black font-semibold rounded-lg py-2.5 transition-colors flex items-center justify-center gap-2"
              >
                {sending && <Loader2 className="w-4 h-4 animate-spin" />}
                <T>Zaregistrovať sa</T>
              </button>
              <p className="text-xs text-gray-500 text-center">
                <T>Už máte účet?</T>{' '}
                <a href="/login" className="text-amber-400 hover:underline">
                  <T>Prihlásiť sa</T>
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
